import { Link, useNavigate } from 'react-router-dom';
import { MoreHorizontal, Trash2, Edit, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import { toast } from 'sonner';

import type { Project } from '@/types';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useNoteStyles } from '@/hooks/useNoteStyles';

interface ProjectCardProps {
  project: Project;
  onDelete: (id: string) => void;
}

export function ProjectCard({ project, onDelete }: ProjectCardProps) {
  const navigate = useNavigate();
  const { styleMap } = useNoteStyles();
  const style = project.note_style_id ? styleMap[project.note_style_id] : undefined;

  const handleOpen = () => {
    navigate(`/project/${project.id}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOpen();
    }
  };

  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <Card
      className="hover:shadow-md hover:border-primary/20 hover:-translate-y-0.5 transition-all group relative cursor-pointer bg-white/70"
      role="button"
      tabIndex={0}
      onClick={handleOpen}
      onKeyDown={handleKeyDown}
    >
      <CardHeader className="pb-2 p-4">
        <div className="flex justify-between items-start gap-2">
          <div className="space-y-1">
            <CardTitle className="line-clamp-1 text-base font-bold">
              <Link
                to={`/project/${project.id}`}
                className="hover:underline decoration-primary/50 underline-offset-4"
                onClick={stopPropagation}
              >
                {project.title}
              </Link>
            </CardTitle>
            <CardDescription className="line-clamp-2 min-h-[2.5em] text-xs">
              {project.description || '暂无描述'}
            </CardDescription>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                className="h-7 w-7 -mr-1 text-muted-foreground"
                onClick={stopPropagation}
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-xl">
              <DropdownMenuItem
                onClick={(e) => {
                  stopPropagation(e);
                }}
                className="text-xs"
              >
                <Edit className="mr-2 h-3.5 w-3.5" /> 编辑
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="text-destructive focus:text-destructive text-xs"
                onClick={async (e) => {
                  stopPropagation(e);
                  if (confirm(`确定要删除项目「${project.title}」吗？\n\n⚠️ 这将同时删除该项目下的所有素材和笔记，此操作不可恢复！`)) {
                    try {
                      await onDelete(project.id);
                      toast.success('项目已删除');
                    } catch (error) {
                      toast.error(error instanceof Error ? error.message : '删除失败');
                    }
                  }
                }}
              >
                <Trash2 className="mr-2 h-3.5 w-3.5" /> 删除
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent className="pb-4 px-4">
        <div className="flex gap-2">
          {style && (
            <Badge variant="secondary" className="text-[10px] font-normal px-2 py-0 h-5 rounded-full bg-secondary/50 border-transparent">
              {style.name}
            </Badge>
          )}
        </div>
      </CardContent>
      <CardFooter className="text-[10px] text-muted-foreground/60 p-4 pt-0 flex items-center gap-1">
        <Calendar className="w-3 h-3" />
        {format(project.created_at, 'yyyy年MM月dd日', { locale: zhCN })}
      </CardFooter>
    </Card>
  );
}

